import { OnboardingBuildingInput } from '../OnboardingBuildingInput';
import { ErrorMessage } from 'components';
import styles from './index.scss';
import { Button } from 'design-system-web';
import { H5 } from 'components/typography';
import { useContinueAsGuest } from 'lane-shared/hooks/useContinueAsGuest';
import { useOnboardingAnalytics } from 'lane-shared/hooks/analytics';
import { Channel } from 'lane-shared/types/ChannelType';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkplaceOnboardingEnabled } from 'lane-shared/hooks/onboarding/useWorkplaceOnboardingEnabled';
import { useOnboardingBuildings } from 'lane-shared/contexts/SignUpContext/useOnboardingBuildings';
import { ActiveChannelTypeEnum } from 'constants-channel';

const TRANSLATION_KEYS = {
  privateBuildingError: 'web.onboarding.building.privateAccess.error',
  serverError: 'web.onboarding.building.server.error',
  createAccountButton: 'shared.onboarding.building.createAccountButton',
  continueButton: 'shared.onboarding.building.continueButton',
  header: 'shared.onboarding.building.location.title',
};

type Props = {
  building: Channel | null;
  onboardingState: 'loading' | 'idle' | 'error';
  userName: string;
  onComplete: (building: Channel, userName: string) => void;
  onContinue: (building: Channel) => void;
  onClear: () => void;
  presumedEmailDomain?: string;
  invitedChannel?: Channel;
};

export function OnboardingSelectBuilding({
  building,
  onboardingState,
  userName,
  onComplete,
  onContinue,
  onClear,
  invitedChannel,
  presumedEmailDomain,
}: Props) {
  const { t } = useTranslation();
  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();
  const { onboardingTracker } = useOnboardingAnalytics();

  const [searchText, setSearchText] = useState('');
  const [selectedBuilding, setBuilding] = useState<Channel | null>(building);

  const {
    buildings,
    isInitialLoading,
    isFetchingMore,
    hasNextPage,
    fetchMore,
  } = useOnboardingBuildings({
    query: searchText,
    presumedEmailDomain,
  });

  const {
    translationKeys: { locationPrompt, locationPlaceholder },
  } = useContinueAsGuest();

  if (
    invitedChannel &&
    selectedBuilding !== invitedChannel &&
    invitedChannel?.type === ActiveChannelTypeEnum.Property
  ) {
    setBuilding(invitedChannel);
  }

  const handleBuildingSearchChange = (text: string) => {
    setSearchText(text);
  };

  const handleBuildingSelectionChange = (buildingObj: Channel | null) => {
    setBuilding(buildingObj);

    if (buildingObj) {
      onboardingTracker.Select.Building({ buildingId: buildingObj?._id });
    }

    if (buildingObj === null) {
      setBuilding(null);
      onClear();
      onboardingTracker.Remove.Building({ buildingId: selectedBuilding?._id! });
    }
  };

  const handleCreateAccount = () => {
    if (selectedBuilding) {
      onComplete(selectedBuilding, userName);
      onboardingTracker.Create.Account({ buildingId: selectedBuilding?._id });
    }
  };

  const privateBuildingError = selectedBuilding?.inviteOnly
    ? new Error(t(TRANSLATION_KEYS.privateBuildingError))
    : null;

  const header = selectedBuilding ? locationPlaceholder : locationPrompt;

  return (
    <div className={styles.container}>
      {privateBuildingError && <ErrorMessage error={privateBuildingError} />}
      <H5>
        {isWorkplaceOnboardingEnabled ? t(TRANSLATION_KEYS.header) : t(header)}
      </H5>
      <div className={styles.searchContainer}>
        <OnboardingBuildingInput
          data={{
            building: selectedBuilding,
            buildingSearch: searchText,
            buildings,
            whiteLabelName: 'whitelabelName',
          }}
          config={{
            isLoading: isInitialLoading,
            isFetchingMore,
            hasNextPage,
            fetchMore,
          }}
          handlers={{
            onBuildingSearchTextChange: handleBuildingSearchChange,
            onBuildingSelectionChange: handleBuildingSelectionChange,
          }}
        />
        {isWorkplaceOnboardingEnabled && (
          <Button
            className={styles.button}
            fullWidth
            onClick={() => {
              if (selectedBuilding) {
                onContinue(selectedBuilding);
              }
            }}
            size="large"
            type="submit"
            loading={onboardingState === 'loading'}
            disabled={selectedBuilding === null}
          >
            {t(TRANSLATION_KEYS.continueButton)}
          </Button>
        )}
        {!isWorkplaceOnboardingEnabled && selectedBuilding && (
          <Button
            className={styles.button}
            fullWidth
            onClick={handleCreateAccount}
            size="large"
            type="submit"
            loading={onboardingState === 'loading'}
          >
            {t(TRANSLATION_KEYS.createAccountButton)}
          </Button>
        )}
      </div>
    </div>
  );
}
